import { useState, useCallback } from "react";
import { toast } from "react-toastify";
import { GetTiss } from "./GetTiss";
import { DocumentReference } from "fhir/r5";

// Tipos para os parâmetros e retorno do hook
interface FetchDocumentReferenceTissParams {
  organization?: string;
  practitionerId?: string;
  patientId?: string;
  appointmentId?: string;
  count?: number;
}

interface Bundle {
  entry?: Array<{ resource: DocumentReference }>;
}

interface UseDocumentReferenceTissReturn {
  documentReferences: DocumentReference[];
  loading: boolean;
  fetchDocumentReferenceTiss: (
    params: FetchDocumentReferenceTissParams
  ) => Promise<{
    documentReferences: DocumentReference[];
  }>;
  clearDocumentReferenceTiss: () => void;
}

// Hook para buscar e gerenciar guias TISS
export const useDocumentReferenceTiss = (): UseDocumentReferenceTissReturn => {
  const [documentReferences, setDocumentReferences] = useState<
    DocumentReference[]
  >([]);
  const [loading, setLoading] = useState(false);

  // Função para buscar guias TISS
  const fetchDocumentReferenceTiss = useCallback(
    async ({
      organization,
      practitionerId,
      patientId,
      appointmentId,
      count,
    }: FetchDocumentReferenceTissParams) => {
      setLoading(true);
      try {
        // Buscar guias TISS com os parâmetros fornecidos
        const response = (await GetTiss({
          organization,
          practitionerId,
          patientId,
          appointmentId,
          count,
        })) as Bundle;

        const documentRefs: DocumentReference[] = [];

        if (response?.entry?.length) {
          // Filtrar apenas os DocumentReferences do tipo TISS
          response.entry.forEach((entry) => {
            const resource = entry.resource;
            if (!resource) return;

            // Verificar se é um DocumentReference do tipo TISS
            const isTissDocument = resource.type?.coding?.some(
              (coding) => coding.code === "tiss"
            );

            if (isTissDocument) {
              documentRefs.push(resource);
            }
          });
        }

        setDocumentReferences(documentRefs);

        return {
          documentReferences: documentRefs,
        };
      } catch (error) {
        // toast.error("Erro ao carregar guias TISS");
        setDocumentReferences([]);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Função para limpar o estado das guias
  const clearDocumentReferenceTiss = useCallback(() => {
    setDocumentReferences([]);
  }, []);

  return {
    documentReferences,
    loading,
    fetchDocumentReferenceTiss,
    clearDocumentReferenceTiss,
  };
};
